/**
 * Converts grain_bin_sites + row visibility prefs into a structured nav item list.
 *
 * The output mirrors the Operations View row ordering and grouping:
 *   - Normal account:   Site > Bin  (2 levels)
 *   - Special viewer:   Account > Site > Bin  (3 levels)
 *
 * Row ordering follows the saved prefs when available (same as Ops View).
 * Hidden bins are excluded by default — toggle APPLY_ROW_VISIBILITY_PREFS to change.
 */

import { PrefJsonItem } from '../operations/manage-sites/ManageSitesTable';
import { getValidOperationsViewRowsVisibilityPrefs } from '../operations/OperationsViewTableHelpers';

// ─── Feature flag ────────────────────────────────────────────────────────────
// Set to false to always show all bins regardless of the hide flag in prefs.
const APPLY_ROW_VISIBILITY_PREFS = true;
// ─────────────────────────────────────────────────────────────────────────────

const UNASSIGNED_BINS_SITE_ID = -1;
const UNASSIGNED_SITE_NAME = 'UNASSIGNED_BINS';

export type NavBinItem = {
  type: 'bin';
  grain_bin_id: number;
  bin_name: string;
  fan_controllers: any[];
  /** Full path used to determine nesting depth in the rendered tree */
  path: string[];
};

export type NavGroupItem = {
  type: 'group';
  /** 0 = account level, 1 = site level */
  depth: number;
  label: string;
  children: NavItem[];
};

export type NavItem = NavBinItem | NavGroupItem;

type GrainBinSite = {
  site_id: number;
  site_name: string;
  account_name: string | null;
  bins: ({
    grain_bin_id: number;
    grain_bin_attributes: {
      bin_name: string;
      site?: { site_id: number; site_name: string } | null;
    };
    fan_controllers: any[];
  } | null)[];
};

/**
 * Builds the ordered, filtered nav item tree from grain_bin_sites + prefs.
 * Returns a flat-ish tree: NavGroupItem nodes contain their children inline.
 */
export function buildNavItemsFromSites(
  grain_bin_sites: GrainBinSite[],
  operations_view_rows_visibility_prefs: any
): NavItem[] {
  const is_aggregated_view = grain_bin_sites.some((s) => s.account_name != null);

  // ── Collect all bins and build lookup maps ──────────────────────────────
  const allBins: {
    grain_bin_id: number;
    grain_bin_attributes: {
      bin_name: string;
      site?: { site_id: number; site_name: string } | null;
    };
    fan_controllers: any[];
    site_id: number;
    account_name: string | null;
  }[] = grain_bin_sites.reduce(
    (acc, site) => {
      site.bins.filter(Boolean).forEach((bin) => {
        acc.push({ ...bin!, site_id: site.site_id, account_name: site.account_name });
      });
      return acc;
    },
    [] as {
      grain_bin_id: number;
      grain_bin_attributes: {
        bin_name: string;
        site?: { site_id: number; site_name: string } | null;
      };
      fan_controllers: any[];
      site_id: number;
      account_name: string | null;
    }[]
  );

  const unassignedBinsIds = allBins
    .filter((b) => {
      const site = b.grain_bin_attributes.site;
      const sid = site ? site.site_id : UNASSIGNED_BINS_SITE_ID;
      return sid === UNASSIGNED_BINS_SITE_ID;
    })
    .map((b) => b.grain_bin_id);

  const allActiveSitesIds = grain_bin_sites.map((s) => s.site_id);

  const liveSitesBins: Record<number, number[]> = {};
  grain_bin_sites.forEach((site) => {
    if (site.site_id !== UNASSIGNED_BINS_SITE_ID) {
      liveSitesBins[site.site_id] = site.bins.filter(Boolean).map((b) => b!.grain_bin_id);
    }
  });

  // ── Resolve prefs ───────────────────────────────────────────────────────
  const validPrefs: PrefJsonItem[] | null = getValidOperationsViewRowsVisibilityPrefs({
    allActiveSitesIds,
    operations_view_rows_visibility_prefs,
    unassignedBinsIds,
    liveSitesBins,
  });

  // ── Build a bin lookup by grain_bin_id ──────────────────────────────────
  const binById = new Map(allBins.map((b) => [b.grain_bin_id, b]));

  // ── Build a site lookup by site_id ──────────────────────────────────────
  const siteById = new Map(grain_bin_sites.map((s) => [s.site_id, s]));

  // ── Helper: make a NavBinItem ───────────────────────────────────────────
  // fan_controllers from grain_bin_sites are FanControllerDto objects:
  //   { id, state, active, fan_attributes, fan_config_state }
  // ContainerListItem expects GrainBinFanController shape:
  //   { alias, fan_controller: { state_next } }
  // Map them here so ContainerListItem's getSimpleFanStatus and hasFanError work correctly.
  const mapFanControllers = (fan_controllers: any[]): any[] => {
    if (!fan_controllers) return [];
    return fan_controllers.map((fc) => ({
      alias: fc.fan_attributes ? fc.fan_attributes.alias : '',
      fan_controller: fc.fan_attributes ? fc.fan_attributes.fan_controller : null,
    }));
  };

  const makeBinItem = (bin: typeof allBins[0], path: string[]): NavBinItem => {
    const fan_controllers = mapFanControllers(bin.fan_controllers);
    const { grain_bin_id } = bin;
    const bin_name = bin.grain_bin_attributes.bin_name;
    return {
      path,
      bin_name,
      fan_controllers,
      grain_bin_id,
      type: 'bin',
    };
  };

  // ── Build ordered bin list per site from prefs ──────────────────────────
  // In aggregated view, skip prefs for UNASSIGNED_BINS (site_id=-1) since each child
  // account has its own -1 entry and prefs only store one -1 entry for the viewer's own bins.
  const getBinsForSite = (site_id: number, accountName: string | null): NavBinItem[] => {
    const site = siteById.get(site_id);
    const is_aggregated = is_aggregated_view;

    if (validPrefs && !(is_aggregated && site_id === UNASSIGNED_BINS_SITE_ID)) {
      const sitePref = validPrefs.find((p) => p.site_id === site_id);
      if (sitePref) {
        return sitePref.bins
          .filter((bp) => !APPLY_ROW_VISIBILITY_PREFS || !bp.hide)
          .reduce((acc: NavBinItem[], bp) => {
            const bin = binById.get(bp.bin_id);
            if (!bin) return acc;
            // Guard against stale prefs: verify the bin still belongs to this site.
            // If a bin was moved to a different site after the pref was saved, skip it
            // here — it will appear under its correct site when that site is processed.
            const liveSiteId = bin.grain_bin_attributes.site
              ? bin.grain_bin_attributes.site.site_id
              : UNASSIGNED_BINS_SITE_ID;
            if (liveSiteId !== site_id) return acc;
            const path = buildBinPath(
              bin.grain_bin_attributes.bin_name,
              site_id,
              accountName,
              grain_bin_sites
            );
            acc.push(makeBinItem(bin, path));
            return acc;
          }, []);
      }
    }
    // No prefs (or aggregated unassigned) — use raw order from the specific site entry
    // In aggregated view, find the site entry matching both site_id AND account_name
    let targetSite: GrainBinSite | undefined;
    if (is_aggregated && accountName) {
      targetSite = grain_bin_sites.find(
        (s) => s.site_id === site_id && s.account_name === accountName
      );
    } else {
      targetSite = site;
    }
    if (!targetSite) return [];
    return targetSite.bins.filter(Boolean).map((b) => {
      const path = buildBinPath(
        b!.grain_bin_attributes.bin_name,
        site_id,
        accountName,
        grain_bin_sites
      );
      return makeBinItem({ ...b!, site_id, account_name: accountName }, path);
    });
  };

  // ── Determine site order ────────────────────────────────────────────────
  // If prefs exist, use pref order; otherwise use grain_bin_sites order.
  const orderedSiteIds: number[] = validPrefs
    ? validPrefs.map((p) => p.site_id).filter((id) => allActiveSitesIds.includes(id))
    : grain_bin_sites.map((s) => s.site_id);

  // ── Build the tree ──────────────────────────────────────────────────────
  if (!is_aggregated_view) {
    // Normal account: Site > Bin
    const items: NavItem[] = [];
    for (const site_id of orderedSiteIds) {
      const site = siteById.get(site_id);
      if (!site) continue;
      const bins = getBinsForSite(site_id, null);
      if (site_id === UNASSIGNED_BINS_SITE_ID) {
        // Ungrouped bins rendered flat (no group header) — or skip if empty
        items.push(...bins);
      } else {
        if (bins.length === 0) continue;
        items.push({ type: 'group', depth: 1, label: site.site_name, children: bins });
      }
    }
    return items;
  }

  // Aggregated view: Account > Site > Bin
  // Group sites by account_name, preserving account order from grain_bin_sites.
  const accountOrder: string[] = [];
  const sitesByAccount = new Map<string, GrainBinSite[]>();
  grain_bin_sites.forEach((site) => {
    const acct = site.account_name || 'Unknown Account';
    if (!sitesByAccount.has(acct)) {
      sitesByAccount.set(acct, []);
      accountOrder.push(acct);
    }
    sitesByAccount.get(acct)!.push(site);
  });

  const items: NavItem[] = [];
  for (const accountName of accountOrder) {
    const accountSites = sitesByAccount.get(accountName)!;

    const siteChildren: NavItem[] = [];
    for (const site of accountSites) {
      const bins = getBinsForSite(site.site_id, accountName);
      if (site.site_id === UNASSIGNED_BINS_SITE_ID) {
        // Ungrouped bins sit directly under the account node
        siteChildren.push(...bins);
      } else {
        if (bins.length === 0) continue;
        siteChildren.push({ type: 'group', depth: 1, label: site.site_name, children: bins });
      }
    }

    if (siteChildren.length === 0) continue;
    items.push({ type: 'group', depth: 0, label: accountName, children: siteChildren });
  }
  return items;
}

/** Builds the siteHierarchy path for a bin (used for display, not AG Grid). */
function buildBinPath(
  bin_name: string,
  site_id: number,
  accountName: string | null,
  grain_bin_sites: GrainBinSite[]
): string[] {
  const is_aggregated = grain_bin_sites.some((s) => s.account_name != null);
  // In aggregated view, match on both site_id AND account_name to avoid picking
  // the wrong entry when multiple child accounts share the same site_id (e.g. -1).
  const siteEntry =
    is_aggregated && accountName
      ? grain_bin_sites.find((s) => s.site_id === site_id && s.account_name === accountName)
      : grain_bin_sites.find((s) => s.site_id === site_id);
  const site_name = siteEntry ? siteEntry.site_name : undefined;
  if (is_aggregated) {
    const acct = accountName || 'Unknown Account';
    if (!site_name || site_name === UNASSIGNED_SITE_NAME) return [acct, bin_name];
    return [acct, site_name, bin_name];
  }
  if (!site_name || site_name === UNASSIGNED_SITE_NAME) return [bin_name];
  return [site_name, bin_name];
}
